import React, { useState, useEffect, useCallback } from 'react';
import { Users, Send, Pause, Play, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, Sparkles } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { apiGet, apiPost, apiPatch } from '../../../utils/api';
import SmartFilter from '../SmartFilter';
import OutreachDraftModal from '../OutreachDraftModal';
import toast from 'react-hot-toast';

// Mirror-derived outreach states (from OutreachEngagement). Blue/orange palette.
const STATE_META = {
  pending:      { label: 'Not contacted', bg: 'bg-gray-100 text-gray-700', dark: 'bg-gray-700 text-gray-300' },
  contacted:    { label: 'Contacted', bg: 'bg-blue-100 text-blue-700', dark: 'bg-blue-900/40 text-blue-300' },
  reminded:     { label: 'Reminded', bg: 'bg-orange-100 text-orange-700', dark: 'bg-orange-900/40 text-orange-300' },
  unresponsive: { label: 'Unresponsive', bg: 'bg-red-100 text-red-700', dark: 'bg-red-900/40 text-red-300' },
  complete:     { label: 'Complete', bg: 'bg-green-100 text-green-700', dark: 'bg-green-900/40 text-green-300' },
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('default', { month: 'short', day: 'numeric' }) : '—');
const stepLabel = (step) => (step === 0 ? 'Initial digest' : `Reminder ${step}`);

const ContactHubTab = ({ auditId }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [rows, setRows] = useState([]);
  const [missingContact, setMissingContact] = useState([]);
  const [summary, setSummary] = useState({ response_rate: 0, contacted: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [busy, setBusy] = useState(null);
  const [filters, setFilters] = useState({});
  const [draftRow, setDraftRow] = useState(null);

  const isSendable = (e) => e.state !== 'complete' && !e.paused;

  const load = useCallback(async () => {
    if (!auditId) return;
    setLoading(true);
    try {
      const r = await apiGet(`/admin/audit_tracker/audits/${auditId}/outreach`);
      if (r.ok) {
        const json = await r.json();
        const engagements = json.engagements || [];
        setRows(engagements);
        setMissingContact(json.missing_contact || []);
        setSummary({ response_rate: json.response_rate || 0, contacted: json.contacted || 0 });
        // Drop any selection that's no longer sendable after the refresh.
        const sendableIds = new Set(engagements.filter(isSendable).map((e) => e.id));
        setSelected((prev) => new Set([...prev].filter((id) => sendableIds.has(id))));
      } else toast.error('Failed to load outreach');
    } catch { toast.error('Failed to load outreach'); }
    finally { setLoading(false); }
  }, [auditId]);

  useEffect(() => { load(); }, [load]);

  const sendNow = async (id) => {
    setBusy(id);
    try {
      const r = await apiPost(`/admin/audit_tracker/outreach_engagements/${id}/send_now`, {});
      if (r.ok) { toast.success('Digest sent'); load(); } else toast.error('Send failed');
    } catch { toast.error('Send failed'); } finally { setBusy(null); }
  };

  const togglePause = async (row) => {
    try {
      const r = await apiPatch(`/admin/audit_tracker/outreach_engagements/${row.id}`, { paused: !row.paused });
      if (r.ok) { toast.success(row.paused ? 'Resumed' : 'Paused'); load(); } else toast.error('Update failed');
    } catch { toast.error('Update failed'); }
  };

  const batchSend = async () => {
    // Only send ids that are still sendable (selection may be stale after a refresh).
    const sendableIds = new Set(rows.filter(isSendable).map((r) => r.id));
    const ids = [...selected].filter((id) => sendableIds.has(id));
    if (!ids.length) { setSelected(new Set()); return; }
    try {
      const r = await apiPost(`/admin/audit_tracker/audits/${auditId}/outreach/batch_send`, { ids });
      if (r.ok) { toast.success(`Sent ${ids.length} digest${ids.length > 1 ? 's' : ''}`); setSelected(new Set()); load(); }
      else toast.error('Batch send failed');
    } catch { toast.error('Batch send failed'); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleExp = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  // Smart-filter rows client-side (the hub loads every engagement for the audit).
  const matchesFilters = (r) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const hay = `${r.contact_name || ''} ${r.contact_email || ''} ${r.owner_company_name || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filters.state && r.state !== filters.state) return false;
    if (filters.paused !== undefined && r.paused !== filters.paused) return false;
    if (filters.min_units_outstanding && (r.units_outstanding || 0) < Number(filters.min_units_outstanding)) return false;
    if (filters.responded !== undefined && !!r.responded !== filters.responded) return false;
    return true;
  };
  const visibleRows = rows.filter(matchesFilters);

  const sendable = rows.filter(isSendable);
  const totalOutstanding = rows.reduce((a, r) => a + (r.units_outstanding || 0), 0);
  const missingProps = missingContact.reduce((a, g) => a + (g.properties?.length || 0), 0);

  const card = `rounded-xl border ${d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`;
  const th = `text-left text-[11px] font-bold uppercase tracking-widest font-rasa px-4 py-2 ${d ? 'text-gray-400' : 'text-gray-500'}`;
  const td = `px-4 py-3 text-[13px] font-rasa ${d ? 'text-gray-200' : 'text-gray-700'}`;

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: 'Contacts', value: rows.length, icon: Users },
          { label: 'Units outstanding', value: totalOutstanding },
          { label: 'Response rate', value: `${summary.response_rate}%`, sub: `${summary.contacted} contacted`, ok: true },
          { label: 'Unresponsive', value: rows.filter((r) => r.state === 'unresponsive').length, alert: true },
          { label: 'Complete', value: rows.filter((r) => r.state === 'complete').length, ok: true },
        ].map((k) => (
          <div key={k.label} className={`${card} p-4`}>
            <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{k.label}</div>
            <div className={`text-2xl font-dm-serif ${k.alert && k.value > 0 ? 'text-red-500' : ''}`}>{k.value}</div>
            {k.sub && <div className={`text-[11px] font-rasa mt-0.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Outstanding work we can't email — no contact on file. */}
      {missingProps > 0 && (
        <div className={`flex items-start gap-2 rounded-xl p-4 border ${d ? 'bg-orange-900/15 border-orange-800/40' : 'bg-orange-50 border-orange-200'}`}>
          <AlertTriangle size={16} className="text-orange-500 mt-0.5 shrink-0" />
          <div className={`text-[13px] font-rasa ${d ? 'text-orange-200' : 'text-orange-800'}`}>
            <strong>{missingProps} propert{missingProps === 1 ? 'y has' : 'ies have'} outstanding documents but no contact email.</strong>{' '}
            Add a primary contact on the Properties tab so they can be chased.
            <div className={`mt-1 text-[12px] ${d ? 'text-orange-300/80' : 'text-orange-700'}`}>
              {missingContact.map((g) => `${g.owner_company_name || 'Unknown owner'} (${g.properties.length})`).join(' · ')}
            </div>
          </div>
        </div>
      )}

      {rows.length === 0 && missingProps === 0 ? (
        <div className={`text-center py-16 rounded-xl border ${d ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
          <CheckCircle2 size={36} className="mx-auto mb-3 text-emerald-500" />
          <p className={`text-sm font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>No outstanding documents — every contact is caught up.</p>
        </div>
      ) : rows.length === 0 ? null : (
        <>
        <SmartFilter context="contacts" onApplyFilters={setFilters} />
        <div className={`${card} overflow-hidden`}>
          {/* Batch bar */}
          <div className={`flex items-center justify-between px-4 py-2.5 border-b ${d ? 'border-gray-700' : 'border-gray-200'}`}>
            <label className={`flex items-center gap-2 text-[12px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>
              <input
                type="checkbox"
                checked={sendable.length > 0 && selected.size === sendable.length}
                onChange={(e) => setSelected(e.target.checked ? new Set(sendable.map((r) => r.id)) : new Set())}
              />
              Select all sendable ({sendable.length})
            </label>
            <button
              onClick={batchSend}
              disabled={selected.size === 0}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-rasa font-medium ${selected.size === 0 ? 'opacity-40 cursor-not-allowed' : ''} bg-blue-600 text-white hover:bg-blue-700`}
            >
              <Send size={13} /> Send selected ({selected.size})
            </button>
          </div>

          <table className="w-full">
            <thead className={d ? 'bg-gray-900/40' : 'bg-gray-50'}>
              <tr>
                <th className={th}></th>
                <th className={th}>Contact</th>
                <th className={th}>Owner</th>
                <th className={th}>Properties</th>
                <th className={th}>Units out</th>
                <th className={th}>State</th>
                <th className={th}>Last sent</th>
                <th className={th}>Next due</th>
                <th className={th}>Assigned</th>
                <th className={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const meta = STATE_META[r.state] || STATE_META.pending;
                const canSend = r.state !== 'complete';
                const isExp = expanded.has(r.id);
                return (
                  <React.Fragment key={r.id}>
                    <tr className={`border-t ${d ? 'border-gray-700/60' : 'border-gray-100'}`}>
                      <td className={td}>
                        {canSend && !r.paused && (
                          <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} />
                        )}
                      </td>
                      <td className={td}>
                        <button onClick={() => toggleExp(r.id)} className="flex items-center gap-1 text-left hover:opacity-70">
                          {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span>
                            <span className="font-medium block">{r.contact_name || r.contact_email}</span>
                            <span className={`text-[11px] ${d ? 'text-gray-500' : 'text-gray-400'}`}>{r.contact_email}</span>
                          </span>
                        </button>
                      </td>
                      <td className={td}>{r.owner_company_name || '—'}</td>
                      <td className={td}>{r.property_count}</td>
                      <td className={td}>{r.units_outstanding}</td>
                      <td className={td}>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${d ? meta.dark : meta.bg}`}>
                          {r.state === 'unresponsive' && <AlertTriangle size={11} />}
                          {meta.label}{r.paused ? ' · paused' : ''}
                        </span>
                      </td>
                      <td className={td}>{fmtDate(r.last_sent_at)}</td>
                      <td className={td}>{r.paused ? '—' : fmtDate(r.next_due_at)}</td>
                      <td className={td}>{r.assigned_to?.name || <span className={d ? 'text-gray-600' : 'text-gray-300'}>—</span>}</td>
                      <td className={td}>
                        <div className="flex items-center gap-1">
                          {canSend && (
                            <button onClick={() => setDraftRow(r)} aria-label={r.custom_message ? 'Edit AI draft (saved)' : 'Draft with AI'} title={r.custom_message ? 'Edit AI draft (saved)' : 'Draft with AI'}
                              className={`p-1.5 rounded-lg ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} ${r.custom_message ? 'text-violet-500' : (d ? 'text-gray-400' : 'text-gray-500')}`}>
                              <Sparkles size={14} />
                            </button>
                          )}
                          {canSend && (
                            <button onClick={() => sendNow(r.id)} disabled={busy === r.id} aria-label="Send now" title="Send now"
                              className={`p-1.5 rounded-lg ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} text-blue-500`}>
                              <Send size={14} />
                            </button>
                          )}
                          {canSend && (
                            <button onClick={() => togglePause(r)} aria-label={r.paused ? 'Resume' : 'Pause'} title={r.paused ? 'Resume' : 'Pause'}
                              className={`p-1.5 rounded-lg ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                              {r.paused ? <Play size={14} /> : <Pause size={14} />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExp && (
                      <tr className={d ? 'bg-gray-900/30' : 'bg-gray-50'}>
                        <td></td>
                        <td className={`${td} pb-3`} colSpan={9}>
                          <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
                            {/* Outstanding properties */}
                            <div>
                              <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-1.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>Outstanding</div>
                              <div className="space-y-1">
                                {(r.properties || []).map((p) => (
                                  <div key={p.processor_property_id} className="flex items-center justify-between">
                                    <span>{p.name}</span>
                                    <span className={`text-[11px] ${d ? 'text-gray-500' : 'text-gray-400'}`}>{p.units_outstanding} units</span>
                                  </div>
                                ))}
                                {(r.properties || []).length === 0 && (
                                  <span className={`text-[12px] ${d ? 'text-gray-500' : 'text-gray-400'}`}>Nothing outstanding{r.responded ? ' — responded' : ''}.</span>
                                )}
                              </div>
                            </div>
                            {/* Activity log */}
                            <div>
                              <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-1.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>Activity</div>
                              <div className="space-y-1">
                                {(r.messages || []).map((m, i) => (
                                  <div key={i} className="flex items-center justify-between">
                                    <span>{stepLabel(m.step)} <span className={`text-[11px] ${d ? 'text-gray-500' : 'text-gray-400'}`}>· {m.units_outstanding} out</span></span>
                                    <span className={`text-[11px] ${d ? 'text-gray-500' : 'text-gray-400'}`}>{fmtDate(m.sent_at)}</span>
                                  </div>
                                ))}
                                {(r.messages || []).length === 0 && (
                                  <span className={`text-[12px] ${d ? 'text-gray-500' : 'text-gray-400'}`}>No emails sent yet.</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {visibleRows.length === 0 && (
                <tr><td colSpan={10} className={`${td} text-center py-8 ${d ? 'text-gray-500' : 'text-gray-400'}`}>No contacts match the filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {draftRow && (
        <OutreachDraftModal
          row={draftRow}
          auditYear={new Date().getFullYear()}
          onClose={() => setDraftRow(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default ContactHubTab;
